import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa uma alocação (projetado) de participante em um business plan
 * 
 * @typedef {Object} AlocacaoResponse
 * @property {number} id - Identificador único da alocação
 * @property {number} business_plan_id - ID do business plan associado
 * @property {number} participante_id - ID do participante alocado
 * @property {string} competencia - Período de competência no formato "YYYY-MM"
 * @property {string} percentual - Percentual de alocação no formato "25.00"
 * @property {string} valor - Valor da alocação no formato "0.00"
 */

/**
 * Representa o custo médio do perfil do participante
 * 
 * @typedef {Object} CustoMedioAlocacao
 * @property {number|null} id - Identificador único do custo médio (null para participante sem projeção ou sem custo médio vigente)
 * @property {number|null} perfil_id - ID do perfil profissional associado
 * @property {string|number} valor - Valor do custo médio (string "25000.00" do banco ou number 0 quando não há custo médio vigente)
 * @property {string|null} competencia_inicio - Início de vigência no formato "YYYY-MM" (pode ser nulo)
 * @property {string|null} competencia_fim - Fim de vigência no formato "YYYY-MM" (pode ser nulo)
 */

/**
 * Representa a calibragem manual de uma alocação
 * 
 * @typedef {Object} CalibragemAlocacao
 * @property {number|null} id - Identificador único da calibragem (null quando não há calibragem cadastrada)
 * @property {string} percentual - Percentual calibrado no formato "50.00" (string, valor padrão "0.00")
 * @property {string|null} justificativa - Justificativa da calibragem (pode ser nulo)
 */

/**
 * Representa o payload de criação/atualização de uma alocação (projetado)
 * 
 * @typedef {Object} AlocacaoPayload
 * @property {number} participante_id - Identificador único do participante
 * @property {string} competencia - Mês de referência no formato "YYYY-MM"
 * @property {number} percentual - Porcentagem de horas dedicadas no mês inteiro. Min: 0. Max: 100
 * @property {number} valor - Custo do participante, proporcional ao custo médio + calibragem pelo percentual
 */

/**
 * Alocações de um participante no Business Plan, agrupadas por competência
 *
 * @typedef {Object} AlocacaoParticipanteResponse
 * @property {number} participante_id - Identificador único do participante
 * @property {string|null} participante_nome - Nome do participante (vagas: "Senioridade | Especialidade")
 * @property {number} tipo_participante - Tipo do participante
 * @property {string} tipo_participante_label - Descrição do tipo do participante
 * @property {string|null} matricula - Matrícula do participante
 * @property {string|null} formacao - Formação do participante
 * @property {string|null} senioridade - Senioridade do perfil atual
 * @property {string|null} especialidade - Especialidade do perfil atual
 * @property {string|null} budget_key - Posição da Budget Key atual
 * @property {CalibragemAlocacao} calibragem - Calibragem do participante no Business Plan
 * @property {Object<string, AlocacaoCompetencia>} competencias - Alocações por competência ("YYYY-MM")
 */

/**
 * Alocação de um participante em uma competência
 *
 * @typedef {Object} AlocacaoCompetencia
 * @property {number|null} id - ID do projetado (null quando não há alocação no mês)
 * @property {string|null} percentual - Porcentagem de horas dedicadas no mês
 * @property {string|null} valor - Valor da alocação no mês
 * @property {CustoMedioAlocacao} custo_medio - Custo médio vigente no mês
 */

const resource = (empresaId, businessPlanId) => `v1/ativacaoped/${empresaId}/business-plans/${businessPlanId}/projetado`;
const LOADING_KEY = 'ativacaoped-business-plan-projetado';

/**
 * Lista as alocações do Business Plan, uma por participante, agrupadas por competência.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<AlocacaoParticipanteResponse[]>} Alocações por participante
 */
export const getAlocacoes = (empresaId, businessPlanId, params = {}) => {
  const query = parseParams(params);
  let url = resource(empresaId, businessPlanId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém uma alocação específica pelo ID.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number|string} alocacaoId - ID da alocação
 * @returns {Promise<AlocacaoResponse>} Dados da alocação
 */
export const getAlocacao = (empresaId, businessPlanId, alocacaoId) => {
  const baseUrl = resource(empresaId, businessPlanId).concat(`/${alocacaoId}`);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl)
  );
};

/**
 * Cria ou atualiza alocações existentes.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {AlocacaoPayload[]} data - Dados parciais das alocações para atualização
 * @returns {Promise<AlocacaoResponse[]>} Alocações atualizadas
 */
export const putAlocacao = (empresaId, businessPlanId, data) => {
  const baseUrl = resource(empresaId, businessPlanId);
  const payload = {
    projetados: data
  };

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, payload).then(({ data }) => data)
  );
}

/**
 * Exclui alocações (projetados) em lote.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number[]} alocacaoIds - Lista de IDs das alocações a serem excluídas
 * @returns {Promise<void>}
 */
export const deleteAlocacoes = (empresaId, businessPlanId, alocacaoIds) => {
  const baseUrl = resource(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl, {
      data: {
        projetado_ids: alocacaoIds
      }
    }).then(({ data }) => data)
  );
}

export default {
  getAlocacao,
  getAlocacoes,
  putAlocacao,
  deleteAlocacoes
};
